import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button, StatusBadge } from '../common';
import { formatDate, isOverdue } from '../../utils/formatters';

/**
 * TopicDetailsModal - Read-only display of topic details
 * Fetches full topic data including action items from the API
 */
const TopicDetailsModal = ({ topicId, onClose }) => {
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTopicData = async () => {
      try {
        const topicData = await api.get(`/topics/${topicId}`);
        setTopic(topicData);
      } catch (error) {
        console.error('Failed to load topic details:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTopicData();
  }, [topicId]);

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal" style={{ maxWidth: '700px' }}>
          <div className="modal-body">
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!topic) return null;

  const isClosed = topic.closedAt || topic.closed;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Topic Details</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{
            background: 'var(--color-primary)',
            color: 'white',
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1rem',
            borderLeft: '4px solid var(--color-orange)'
          }}>
            <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>{topic.title}</h3>
            <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>
              <span>Meeting: {topic.meetingName || 'Unknown'}</span>
              <span style={{ margin: '0 0.5rem' }}>•</span>
              <span>Submitted by: {topic.creatorName}</span>
              <span style={{ margin: '0 0.5rem' }}>•</span>
              <span>{new Date(topic.createdAt).toLocaleDateString()}</span>
            </div>
          </div>

          {topic.description && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--color-navy)', marginBottom: '0.5rem' }}>Description</h4>
              <div 
                className="topic-description"
                style={{
                  padding: '0.75rem',
                  background: 'var(--color-bg)',
                  borderRadius: '4px',
                  fontSize: '0.9rem',
                  whiteSpace: 'pre-wrap'
                }}
              >
                {topic.description}
              </div>
            </div>
          )}

          {topic.discussionNotes && (
            <div style={{ marginBottom: '1rem' }}>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--color-navy)', marginBottom: '0.5rem' }}>Discussion Notes</h4>
              <div style={{
                padding: '0.75rem',
                background: 'var(--color-primary-light)',
                borderRadius: '4px',
                borderLeft: '3px solid var(--color-primary)',
                fontSize: '0.9rem',
                whiteSpace: 'pre-wrap'
              }}>
                {topic.discussionNotes}
              </div>
            </div>
          )}

          <div style={{ marginBottom: '1rem' }}>
            <h4 style={{ fontSize: '0.9rem', color: 'var(--color-navy)', marginBottom: '0.5rem' }}>Status</h4>
            <span className={`badge ${isClosed ? 'badge-closed' : 'badge-open'}`} style={{ fontSize: '0.85rem', padding: '0.25rem 0.75rem' }}>
              {isClosed ? (topic.closedAt ? `Closed on ${new Date(topic.closedAt).toLocaleDateString()}` : 'Closed') : 'Open'}
            </span>
          </div>

          {topic.todos && topic.todos.length > 0 && (
            <div>
              <h4 style={{ fontSize: '0.9rem', color: 'var(--color-navy)', marginBottom: '0.5rem' }}>
                Action Items ({topic.todos.filter(t => t.status === 'Done').length}/{topic.todos.length} completed)
              </h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Assigned To</th>
                    <th>Due Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {topic.todos.map(todo => {
                    const assigneeNames = (todo.assignees || [])
                      .map(a => `${a.firstName} ${a.lastName}`)
                      .join(', ');
                    const overdueCheck = isOverdue(todo);
                    return (
                      <tr key={todo.id}>
                        <td>{todo.title}</td>
                        <td>{assigneeNames || 'Unassigned'}</td>
                        <td>
                          {todo.dueDate ? (
                            <span style={{
                              color: overdueCheck ? 'var(--color-danger)' : 'inherit',
                              fontWeight: overdueCheck ? 'bold' : 'normal'
                            }}>
                              {formatDate(todo.dueDate)}
                              {overdueCheck && ' ⚠️'}
                            </span>
                          ) : '-'}
                        </td>
                        <td><StatusBadge status={todo.status} type="todo" /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {(!topic.todos || topic.todos.length === 0) && (
            <div style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              No action items for this topic
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
};

TopicDetailsModal.propTypes = {
  topicId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default TopicDetailsModal;
